import React, { useState, useEffect, useRef } from 'react';

const QueueFilter = ({
    queues = [],
    selectedQueues = [],
    onChange,
    placeholder = "Select Queues"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSelectedQueues, setTempSelectedQueues] = useState(selectedQueues);
    const dropdownRef = useRef(null);

    // Filter queues based on search term (client-side search)
    const filteredQueues = queues.filter(queue =>
        queue.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Reset temp selection when props change
    useEffect(() => {
        setTempSelectedQueues(selectedQueues);
    }, [selectedQueues]);

    const handleCheckboxChange = (queue) => {
        setTempSelectedQueues(prev => {
            const isSelected = prev.some(q => q.value === queue.value);
            if (isSelected) {
                return prev.filter(q => q.value !== queue.value);
            } else {
                return [...prev, queue];
            }
        });
    };

    const handleSubmit = () => {
        onChange(tempSelectedQueues);
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
    };

    const handleCancel = () => {
        setTempSelectedQueues(selectedQueues);
        setSearchTerm('');
        setIsOpen(false);
    };

    const handleSelectAll = () => {
        setTempSelectedQueues([...filteredQueues]);
    };

    const handleClearAll = () => {
        setTempSelectedQueues([]);
    };

    const displayText = selectedQueues.length === 0
        ? placeholder
        : `${selectedQueues.length} queue${selectedQueues.length !== 1 ? 's' : ''} selected`;

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger button - styled to match react-select */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="w-full min-h-[38px] px-3 py-2 bg-white border border-gray-300 rounded cursor-pointer flex justify-between items-center hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                style={{
                    fontSize: '14px',
                    lineHeight: '1.5'
                }}
            >
                <span className={selectedQueues.length === 0 ? "text-gray-500" : "text-gray-900"}>
                    {displayText}
                </span>
                <div className="flex items-center space-x-2">
                    {selectedQueues.length > 0 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange([]);
                            }}
                            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                            title="Clear all"
                        >
                            ×
                        </button>
                    )}
                    <span className="text-gray-400 text-sm">
                        {isOpen ? '▲' : '▼'}
                    </span>
                </div>
            </div>

            {/* Dropdown panel */}
            {isOpen && (
                <div
                    className="absolute top-full left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded shadow-lg z-[1000]"
                    style={{
                        width: 'auto',
                        minWidth: '355px',
                        maxWidth: '550px',
                        maxHeight: '400px'
                    }}
                >
                    {/* Search input */}
                    <div className="p-3 border-b border-gray-200">
                        <input
                            type="text"
                            placeholder="Search queues..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                            autoFocus
                        />
                    </div>

                    {/* Action buttons */}
                    <div className="px-3 py-2 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                        <div className="space-x-3">
                            <button
                                onClick={handleSelectAll}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                disabled={filteredQueues.length === 0}
                            >
                                Select All ({filteredQueues.length})
                            </button>
                            <button
                                onClick={handleClearAll}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                                Clear All
                            </button>
                        </div>
                        <div className="text-xs text-gray-600 font-medium">
                            {tempSelectedQueues.length} selected
                        </div>
                    </div>

                    {/* Queue list */}
                    <div className="max-h-48 overflow-y-auto">
                        {filteredQueues.length === 0 ? (
                            <div className="p-4 text-gray-500 text-sm text-center">
                                {searchTerm ? `No queues found for "${searchTerm}"` : 'No queues available'}
                            </div>
                        ) : (
                            filteredQueues.map((queue) => {
                                const isSelected = tempSelectedQueues.some(q => q.value === queue.value);
                                return (
                                    <label
                                        key={queue.value}
                                        className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleCheckboxChange(queue)}
                                            className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm flex-1 text-gray-900">{queue.label}</span>
                                        {queue.count !== undefined && (
                                            <span className="text-xs text-gray-500 ml-2">({queue.count})</span>
                                        )}
                                    </label>
                                );
                            })
                        )}
                    </div>

                    {/* Submit/Cancel buttons */}
                    <div className="p-3 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50">
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 text-gray-700 font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                        >
                            Apply ({tempSelectedQueues.length})
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QueueFilter;